import { Quote, QuoteItem, QuoteStatus, ServiceAssignment } from '../types';
import { isQuoteExpired, isQuoteItemExcluded, lineTotal } from './quoteLogic';
import { isImportedQuote } from './quoteDocumentImport';
import { FOLLOW_UP_STATUSES } from './quoteFollowUp';
import { effectiveTranches, isQuoteOrdered, itemOrderStates, quoteOrderSummary } from './quoteOrderTranches';

// Kennzahlen fuer das Angebots-KPI-Dashboard. Bewusst pure (keine React-,
// Supabase- oder Date.now()-Abhaengigkeit): "heute" wird immer hereingereicht,
// damit die Funktionen ohne DOM-Testumgebung pruefbar sind.
//
// Zwei Sorten Kennzahlen:
//   - Stichtagsgroessen (offene Pipeline, faellige Nachfassaktionen, Entwuerfe)
//     beziehen sich immer auf "heute" und ignorieren das Zeitfenster.
//   - Flussgroessen (Erfolgsquote, gewonnenes Volumen, Abgelaufenes) zaehlen nur
//     Angebote, deren ENTSCHEIDUNGSDATUM im Fenster liegt.

/** Zeitraum-Voreinstellungen der KPI-Leiste in der Angebotsliste. */
export type QuotePeriod = '90d' | 'year' | 'all';

/** Inklusives Datumsfenster, beide Grenzen als ISO-Datum (YYYY-MM-DD). */
export interface DateWindow {
    from?: string;
    to?: string;
}

export interface QuoteStats {
    // ── Stichtag (heute) ────────────────────────────────────────────────────
    /** Versendet und noch gueltig – steht gerade zur Entscheidung. */
    openCount: number;
    openNet: number;
    /** Versendet, nicht erledigt, Nachfassdatum erreicht (= fetchDueFollowUps). */
    dueFollowUpCount: number;
    draftCount: number;
    draftNet: number;
    /** Alter des aeltesten Entwurfs in Tagen, null wenn es keinen gibt. */
    oldestDraftDays: number | null;

    // ── Zeitraum (Entscheidungsdatum im Fenster) ────────────────────────────
    decidedCount: number;
    decidedNet: number;
    wonCount: number;
    wonNet: number;
    /** Wertbasierte Erfolgsquote in Prozent. null = kein Nenner (nichts entschieden). */
    winRatePct: number | null;
    /** Anzahlbasierte Erfolgsquote in Prozent – Untertitel zur wertbasierten. */
    winRateCountPct: number | null;
    /** Durchschnittliches Nettovolumen je gewonnenem Angebot. */
    avgWonNet: number | null;
    /** Versendet, nie entschieden, Gueltigkeit abgelaufen. */
    expiredCount: number;
    expiredNet: number;
    /** Ø Tage von Versand bis Entscheidung. */
    avgDecisionDays: number | null;

    // ── Herkunft (Zeitraum, Anker Angebotsdatum) ────────────────────────────
    /**
     * Eingelesene Alt-/Fremdangebote im Fenster. Sie zaehlen ueberall mit —
     * die Kennzahl sagt nur, wie viel des Volumens nachgetragener Bestand ist
     * und nicht frische Akquise.
     */
    importedCount: number;
    importedNet: number;
}

export interface MonthlyVolumePoint {
    /** Sortierschluessel, Format YYYY-MM. */
    month: string;
    /** Anzeigetext, z. B. "Jul 26". */
    label: string;
    won: number;
    open: number;
    lost: number;
}

export interface QuoteFunnel {
    createdCount: number;
    createdNet: number;
    sentCount: number;
    sentNet: number;
    acceptedCount: number;
    acceptedNet: number;
    /** Anteil der erstellten Angebote, die tatsaechlich rausgegangen sind. */
    sentRatePct: number | null;
    /** Anteil der versendeten Angebote, die angenommen wurden. */
    winRatePct: number | null;
}

export interface CustomerQuoteStats {
    customerId: string;
    name: string;
    quoteCount: number;
    quoteNet: number;
    wonCount: number;
    wonNet: number;
    /** Wertbasierte Erfolgsquote des Kunden. null = beim Kunden nichts entschieden. */
    winRatePct: number | null;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/** Maximale Balkenzahl im Monatschart – ohne Deckel spannt "Alle" beliebig weit. */
const MAX_MONTHS = 24;

const day = (iso?: string | null): string | undefined => (iso ? iso.slice(0, 10) : undefined);

/** netTotal aus der DB kann null oder ein nicht-numerischer String sein. */
const num = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const pct = (part: number, total: number): number | null =>
    total > 0 ? (part / total) * 100 : null;

const inWindow = (date: string | undefined, w: DateWindow): boolean => {
    if (!date) return false;
    if (w.from && date < w.from) return false;
    if (w.to && date > w.to) return false;
    return true;
};

const addDays = (iso: string, days: number): string => {
    const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};

const daysBetween = (fromIso: string, toIso: string): number =>
    Math.round(
        (Date.parse(`${toIso.slice(0, 10)}T00:00:00Z`) - Date.parse(`${fromIso.slice(0, 10)}T00:00:00Z`)) / 86_400_000,
    );

/**
 * Effektiver Status: ein versendetes Angebot, dessen Gueltigkeit verstrichen
 * ist, zaehlt als 'expired' – auch wenn in der DB noch 'sent' steht.
 */
export const effectiveQuoteStatus = (q: Quote, todayIso: string): QuoteStatus =>
    q.status === 'sent' && isQuoteExpired(q, todayIso) ? 'expired' : q.status;

/** Zeitfenster einer Voreinstellung. '90d' schliesst den heutigen Tag mit ein. */
export const periodWindow = (period: QuotePeriod, todayIso: string): DateWindow => {
    const today = todayIso.slice(0, 10);
    if (period === 'all') return {};
    if (period === '90d') return { from: addDays(today, -89), to: today };
    return { from: `${today.slice(0, 4)}-01-01`, to: today };
};

/**
 * Datum, an dem ueber das Angebot entschieden wurde. undefined = noch offen
 * (Entwurf oder versendet und gueltig).
 *
 * Altbestand aus dem Vorsystem hat teils kein acceptedAt/rejectedAt – dann
 * greift updatedAt und zuletzt das Angebotsdatum, damit solche Angebote nicht
 * still aus jeder Auswertung fallen.
 */
export const decisionDate = (q: Quote, todayIso: string): string | undefined => {
    const eff = effectiveQuoteStatus(q, todayIso);
    // Teilbeauftragt: entschieden ist ab dem ERSTEN Auftragsteil (acceptedAt).
    if (eff === 'accepted' || eff === 'partial') return day(q.acceptedAt) || day(q.updatedAt) || day(q.quoteDate);
    if (eff === 'rejected') return day(q.rejectedAt) || day(q.updatedAt) || day(q.quoteDate);
    if (eff === 'expired') return day(q.validUntil) || day(q.updatedAt) || day(q.quoteDate);
    return undefined;
};

/**
 * Gewonnenes/verlorenes Volumen eines (teil)beauftragten Angebots, je
 * Auftragsteil an DESSEN Datum: Teil 1 im März und der Rest im Oktober zählen
 * als Auftragseingang im März bzw. Oktober. „Rest entfällt" ist verlorenes
 * Volumen an seinem Datum. Altbestand (angenommen ohne Teile) = ein Teil mit
 * der Angebotssumme am Annahmedatum — dieselbe Zahl wie bisher.
 */
const trancheEvents = (q: Quote): { day: string; won: number; lost: number }[] =>
    effectiveTranches(q).map((t) => {
        const d = t.orderDate || day(t.at) || day(q.acceptedAt) || day(q.updatedAt) || day(q.quoteDate) || '';
        const net = num(t.netAmount);
        return t.kind === 'decline' ? { day: d, won: 0, lost: net } : { day: d, won: net, lost: 0 };
    });

export const computeQuoteStats = (
    quotes: Quote[],
    opts: { today: string } & DateWindow,
): QuoteStats => {
    const today = opts.today.slice(0, 10);
    const w: DateWindow = { from: opts.from, to: opts.to };

    let openCount = 0, openNet = 0;
    let dueFollowUpCount = 0;
    let draftCount = 0, draftNet = 0;
    let oldestDraftDays: number | null = null;

    let decidedCount = 0, decidedNet = 0;
    let wonCount = 0, wonNet = 0;
    // Fuer den Durchschnitt je gewonnenem Angebot: Betrag aus DERSELBEN Menge
    // wie wonCount (Angebote mit erster Entscheidung im Fenster), nicht aus den
    // Auftragsteilen im Fenster — sonst zaehlte ein im Oktober beauftragter
    // Rest eines Maerz-Angebots im Zaehler, aber nicht im Nenner.
    let wonNetOfCounted = 0;
    let expiredCount = 0, expiredNet = 0;
    let decisionDaysSum = 0, decisionDaysCount = 0;
    let importedCount = 0, importedNet = 0;

    for (const q of quotes) {
        const eff = effectiveQuoteStatus(q, today);
        const net = num(q.netTotal);

        // Herkunft am Angebotsdatum, nicht am Entscheidungsdatum: gefragt ist
        // "wie viel des Bestands im Fenster ist nachgetragen", unabhaengig
        // davon, ob darueber schon entschieden wurde.
        if (isImportedQuote(q) && inWindow(day(q.quoteDate), w)) {
            importedCount += 1;
            importedNet += net;
        }

        // ── Stichtag ────────────────────────────────────────────────────────
        if (eff === 'sent') {
            openCount += 1;
            openNet += net;
        }
        // Teilbeauftragt: der offene REST steht noch zur Entscheidung.
        if (eff === 'partial') {
            openCount += 1;
            openNet += quoteOrderSummary(q).openNet;
        }
        if (eff === 'draft') {
            draftCount += 1;
            draftNet += net;
            const created = day(q.createdAt);
            if (created) {
                const age = daysBetween(created, today);
                if (oldestDraftDays === null || age > oldestDraftDays) oldestDraftDays = age;
            }
        }
        // Gleiche Bedingung wie fetchDueFollowUps: roher Status aus
        // FOLLOW_UP_STATUSES, damit Kachel und Nachfass-Panel nie auseinanderlaufen.
        const followUp = day(q.followUpDate);
        if (FOLLOW_UP_STATUSES.includes(q.status) && !q.followUpDoneAt && followUp && followUp <= today) {
            dueFollowUpCount += 1;
        }

        // ── Zeitraum ────────────────────────────────────────────────────────
        const decided = decisionDate(q, today);
        // (Teil)beauftragt: Volumen je Auftragsteil an dessen Datum, die
        // Anzahl einmal am Datum der ersten Entscheidung.
        if (eff === 'accepted' || eff === 'partial') {
            for (const ev of trancheEvents(q)) {
                if (!inWindow(ev.day, w)) continue;
                decidedNet += ev.won + ev.lost;
                wonNet += ev.won;
            }
        }
        if (!decided || !inWindow(decided, w)) continue;

        decidedCount += 1;
        if (eff === 'accepted' || eff === 'partial') {
            wonCount += 1;
            wonNetOfCounted += quoteOrderSummary(q).orderedNet;
        } else {
            decidedNet += net;
        }
        if (eff === 'expired') {
            expiredCount += 1;
            expiredNet += net;
        }
        const sent = day(q.sentAt);
        if (sent) {
            decisionDaysSum += Math.max(0, daysBetween(sent, decided));
            decisionDaysCount += 1;
        }
    }

    return {
        openCount,
        openNet,
        dueFollowUpCount,
        draftCount,
        draftNet,
        oldestDraftDays,
        decidedCount,
        decidedNet,
        wonCount,
        wonNet,
        winRatePct: pct(wonNet, decidedNet),
        winRateCountPct: pct(wonCount, decidedCount),
        avgWonNet: wonCount > 0 ? wonNetOfCounted / wonCount : null,
        expiredCount,
        expiredNet,
        avgDecisionDays: decisionDaysCount > 0 ? decisionDaysSum / decisionDaysCount : null,
        importedCount,
        importedNet,
    };
};

// ─── Positionsebene: worin steckt das Angebotsvolumen? ──────────────────────
//
// Bis hierher wertet jede Kennzahl nur die Angebotssumme (netTotal) aus. Die
// Frage "wie viel Volumen steckt in Begehungen, wie viel im SiGe-Plan" laesst
// sich damit nicht beantworten — dafuer muss auf die Positionen heruntergerechnet
// werden. Grundlage ist derselbe Zeilenbetrag wie in der Angebotssumme
// (quoteLogic.lineTotal), der Gesamtnachlass wird anteilig eingerechnet und
// Eventualpositionen (NEP) bleiben aussen vor — genau wie in calculateQuoteTotals.

/** Anzeigenamen der Leistungsarten. 'none' = von der KI nicht zugeordnet. */
export const ASSIGNMENT_LABELS: Record<ServiceAssignment, string> = {
    none: 'Ohne Zuordnung',
    inspection: 'Begehungen',
    einweisung: 'Einweisungen SiGe-Plan',
    sigeplan: 'SiGe-Plan',
    asplan: 'A+S-Plan (kontaminierte Bereiche)',
    unterlage: 'Unterlage (RAB 32)',
    vorank: 'Vorankündigung',
    site_rules: 'Baustellenordnung',
    planning_general: 'Planung allgemein',
    additional: 'Sonstiges / Nebenkosten',
    flatrate: 'Monatspauschale',
    unterweisung: 'Unterweisungen (SiFa)',
    gbu: 'Gefährdungsbeurteilung (SiFa)',
};

export interface QuoteVolumeSlice {
    /** Schluessel der Gruppe: ServiceAssignment bzw. Abschnittstitel. */
    key: string;
    label: string;
    /** Nettovolumen aller Angebote im Fenster (nach Positions- und Gesamtrabatt). */
    net: number;
    /** Davon aus angenommenen Angeboten. */
    wonNet: number;
    /** Anzahl Positionen und Anzahl Angebote, in denen die Gruppe vorkommt. */
    itemCount: number;
    quoteCount: number;
}

/** Zeilenbetrag inkl. anteiligem Gesamtnachlass des Angebots. */
const netOfItem = (item: QuoteItem, discountPercent: number): number =>
    lineTotal(item) * (1 - (discountPercent || 0) / 100);

/**
 * Gemeinsamer Kern beider Aufschluesselungen. `groupOf` liefert Schluessel und
 * Anzeigename je Position; Positionen ohne Gruppe fallen in den vom Aufrufer
 * bestimmten Sammelposten.
 */
const sliceVolume = (
    quotes: Quote[],
    opts: { today: string } & DateWindow,
    groupOf: (item: QuoteItem, quote: Quote) => { key: string; label: string },
): QuoteVolumeSlice[] => {
    const today = opts.today.slice(0, 10);
    const w: DateWindow = { from: opts.from, to: opts.to };

    const acc = new Map<string, QuoteVolumeSlice & { quotes: Set<string> }>();
    for (const q of quotes) {
        if (!inWindow(day(q.quoteDate), w)) continue;
        // Gewonnen ist der BEAUFTRAGTE Anteil je Position (Teilbeauftragung).
        const ordered = isQuoteOrdered(effectiveQuoteStatus(q, today));
        const states = ordered ? itemOrderStates(q) : null;
        const discount = num(q.discountPercent);

        for (const item of q.items || []) {
            // NEP zaehlt nicht: es ist nicht beauftragt und steckt auch nicht
            // in netTotal — sonst summierten sich die Scheiben nicht zur
            // Angebotssumme auf. Gestrichene Positionen aus demselben Grund:
            // sie werden gar nicht mehr angeboten.
            if (item.nep || isQuoteItemExcluded(item)) continue;
            const { key, label } = groupOf(item, q);
            let row = acc.get(key);
            if (!row) {
                row = { key, label, net: 0, wonNet: 0, itemCount: 0, quoteCount: 0, quotes: new Set() };
                acc.set(key, row);
            }
            const net = netOfItem(item, discount);
            row.net += net;
            const st = states?.get(item.id);
            if (st && st.offered > 0) row.wonNet += net * Math.min(1, st.ordered / st.offered);
            row.itemCount += 1;
            row.quotes.add(q.id);
        }
    }

    return [...acc.values()]
        .map(({ quotes: qs, ...row }) => ({
            ...row,
            net: Math.round(row.net * 100) / 100,
            wonNet: Math.round(row.wonNet * 100) / 100,
            quoteCount: qs.size,
        }))
        .sort((a, b) => b.net - a.net || a.label.localeCompare(b.label));
};

/**
 * Angebotsvolumen je Leistungsart (QuoteItem.assignment). Positionen ohne
 * Zuordnung laufen unter 'none' — sie werden nicht auf die anderen verteilt,
 * sonst waere die Aufschluesselung geraten.
 */
export const volumeByAssignment = (
    quotes: Quote[],
    opts: { today: string } & DateWindow,
): QuoteVolumeSlice[] =>
    sliceVolume(quotes, opts, (item) => {
        const key = (item.assignment ?? 'none') as ServiceAssignment;
        return { key, label: ASSIGNMENT_LABELS[key] ?? ASSIGNMENT_LABELS.none };
    });

/**
 * Angebotsvolumen je LV-Abschnitt (Sektion des Angebots). Der Titel kommt aus
 * details.sections; Positionen ohne Sektion sammeln sich unter "Ohne Abschnitt".
 *
 * Die Gruppierung laeuft ueber den TITEL, nicht ueber die Sektions-ID: die ID
 * ist je Angebot neu vergeben, gleichnamige Abschnitte verschiedener Angebote
 * gehoeren aber in dieselbe Scheibe.
 */
export const volumeBySection = (
    quotes: Quote[],
    opts: { today: string } & DateWindow,
): QuoteVolumeSlice[] => {
    const titles = new Map<string, Map<string, string>>();
    for (const q of quotes) {
        const map = new Map<string, string>();
        for (const s of q.details?.sections || []) map.set(s.id, s.title);
        titles.set(q.id, map);
    }
    return sliceVolume(quotes, opts, (item, quote) => {
        const title = (item.sectionId && titles.get(quote.id)?.get(item.sectionId)) || '';
        const t = title.trim();
        return t ? { key: t.toLowerCase(), label: t } : { key: '', label: 'Ohne Abschnitt' };
    });
};

/**
 * Angebotsvolumen je Monat, verankert am Angebotsdatum. Zeigt den zeitlichen
 * Verlauf des Auftragseingangs, aufgeteilt nach heutigem Ausgang.
 *
 * Ohne Fenstergrenzen wird die Spanne aus den Daten abgeleitet und auf die
 * letzten MAX_MONTHS Monate begrenzt.
 */
export const monthlyQuoteVolume = (
    quotes: Quote[],
    opts: { today: string } & DateWindow,
): MonthlyVolumePoint[] => {
    const today = opts.today.slice(0, 10);
    const w: DateWindow = { from: opts.from, to: opts.to };

    const buckets = new Map<string, MonthlyVolumePoint>();
    for (const q of quotes) {
        const date = day(q.quoteDate);
        if (!date || !inWindow(date, w)) continue;
        const month = date.slice(0, 7);
        let b = buckets.get(month);
        if (!b) {
            const monthIndex = Number(month.slice(5, 7)) - 1;
            b = {
                month,
                label: `${MONTHS_SHORT[monthIndex] ?? month.slice(5, 7)} ${month.slice(2, 4)}`,
                won: 0,
                open: 0,
                lost: 0,
            };
            buckets.set(month, b);
        }
        const eff = effectiveQuoteStatus(q, today);
        const net = num(q.netTotal);
        if (eff === 'accepted' || eff === 'partial') {
            // Teilbeauftragt: beauftragt / entfallen / noch offen getrennt.
            const sum = quoteOrderSummary(q);
            b.won += sum.orderedNet;
            b.lost += sum.declinedNet;
            b.open += sum.openNet;
        } else if (eff === 'rejected' || eff === 'expired') b.lost += net;
        else b.open += net;
    }

    const points = [...buckets.values()].sort((a, b) => a.month.localeCompare(b.month));
    return points.slice(-MAX_MONTHS);
};

/**
 * Kohorten-Trichter: Basis sind die im Fenster ERSTELLTEN Angebote, verfolgt
 * bis Versand und Annahme. So misst jede Stufe dieselbe Grundmenge – bei
 * getrennten Datumsankern je Stufe waeren die Quoten nicht vergleichbar.
 */
export const quoteFunnel = (
    quotes: Quote[],
    opts: { today: string } & DateWindow,
): QuoteFunnel => {
    const today = opts.today.slice(0, 10);
    const w: DateWindow = { from: opts.from, to: opts.to };

    let createdCount = 0, createdNet = 0;
    let sentCount = 0, sentNet = 0;
    let acceptedCount = 0, acceptedNet = 0;

    for (const q of quotes) {
        if (!inWindow(day(q.quoteDate), w)) continue;
        const net = num(q.netTotal);
        createdCount += 1;
        createdNet += net;

        // "Ist rausgegangen": Versanddatum gesetzt oder der Status hat den
        // Entwurf verlassen (Direktannahme ohne Versand ist erlaubt).
        if (q.sentAt || q.status !== 'draft') {
            sentCount += 1;
            sentNet += net;
        }
        if (isQuoteOrdered(effectiveQuoteStatus(q, today))) {
            acceptedCount += 1;
            acceptedNet += quoteOrderSummary(q).orderedNet;
        }
    }

    return {
        createdCount,
        createdNet,
        sentCount,
        sentNet,
        acceptedCount,
        acceptedNet,
        sentRatePct: pct(sentCount, createdCount),
        winRatePct: pct(acceptedCount, sentCount),
    };
};

/**
 * Kunden nach gewonnenem Volumen im Fenster (Anker: Angebotsdatum).
 * Die Erfolgsquote je Kunde ist wertbasiert und zaehlt nur entschiedene
 * Angebote – noch laufende druecken die Quote sonst kuenstlich.
 */
export const topCustomersByQuotes = (
    quotes: Quote[],
    opts: { today: string; limit?: number } & DateWindow,
): CustomerQuoteStats[] => {
    const today = opts.today.slice(0, 10);
    const limit = opts.limit ?? 10;
    const w: DateWindow = { from: opts.from, to: opts.to };

    const acc = new Map<string, CustomerQuoteStats & { decidedNet: number }>();
    for (const q of quotes) {
        if (!inWindow(day(q.quoteDate), w)) continue;
        const key = q.customerId || 'unknown';
        let row = acc.get(key);
        if (!row) {
            row = {
                customerId: key,
                name: q.customerName || '—',
                quoteCount: 0,
                quoteNet: 0,
                wonCount: 0,
                wonNet: 0,
                winRatePct: null,
                decidedNet: 0,
            };
            acc.set(key, row);
        }
        // Nur ein Teil der Angebote traegt den Kundennamen (Join in mapQuoteRow).
        if (row.name === '—' && q.customerName) row.name = q.customerName;

        const eff = effectiveQuoteStatus(q, today);
        const net = num(q.netTotal);
        row.quoteCount += 1;
        row.quoteNet += net;
        if (eff === 'accepted' || eff === 'partial') {
            // Entschieden ist nur der beauftragte und der entfallene Teil.
            const sum = quoteOrderSummary(q);
            row.decidedNet += sum.orderedNet + sum.declinedNet;
            row.wonCount += 1;
            row.wonNet += sum.orderedNet;
        } else if (eff === 'rejected' || eff === 'expired') row.decidedNet += net;
    }

    return [...acc.values()]
        .map(({ decidedNet, ...row }) => ({ ...row, winRatePct: pct(row.wonNet, decidedNet) }))
        .sort((a, b) => b.wonNet - a.wonNet || b.quoteNet - a.quoteNet)
        .slice(0, limit);
};
